import type { BackgroundsPanelSettings } from '@/lib/backgrounds/backgrounds-panel-settings'
import { BackgroundsPanel } from '../../backgrounds-panel/backgrounds-panel'
import { BooksPanel } from '../../books-panel/books-panel'
import { ClikArtPanel } from '../../clikart-panel/clikart-panel'
import { NewsPanel } from '../../news-panel/news-panel'
import { PhotosPanel, type PhotosPanelEscapeStateRef } from '../../photos-panel/photos-panel'
import { PortraitsPanel } from '../../portraits-panel/portraits-panel'
import { TemplatesPanel } from '../../templates-panel/templates-panel'
import { RIGHT_PANEL_IDS } from '../constants/panel-ids'
import type { useClikArtFilters } from '../hooks/use-clikart-filters'
import type { usePortraitsFilters } from '../hooks/use-portraits-filters'
import type { useTemplatesFilters } from '../hooks/use-templates-filters'

interface PanelBodyProps {
  activePanel: string | null
  spreadId: string | null
  onPhotosDropdownOpenChange: (open: boolean) => void
  photosEscapeStateRef: PhotosPanelEscapeStateRef
  clikArtFilters: ReturnType<typeof useClikArtFilters>
  onClikArtDropdownOpenChange: (open: boolean) => void
  templatesFilters: ReturnType<typeof useTemplatesFilters>
  onTemplatesDropdownOpenChange: (open: boolean) => void
  backgroundsSettings: BackgroundsPanelSettings
  onBackgroundsSettingsChange: (settings: BackgroundsPanelSettings) => void
  onBackgroundsDropdownOpenChange: (open: boolean) => void
  portraitsFilters: ReturnType<typeof usePortraitsFilters>
  onPortraitsDropdownOpenChange: (open: boolean) => void
}

/** The open right-side panel's content, by `activePanel`. */
export function PanelBody({
  activePanel,
  spreadId,
  onPhotosDropdownOpenChange,
  photosEscapeStateRef,
  clikArtFilters,
  onClikArtDropdownOpenChange,
  templatesFilters,
  onTemplatesDropdownOpenChange,
  backgroundsSettings,
  onBackgroundsSettingsChange,
  onBackgroundsDropdownOpenChange,
  portraitsFilters,
  onPortraitsDropdownOpenChange,
}: PanelBodyProps) {
  switch (activePanel) {
    case RIGHT_PANEL_IDS.photos:
      return (
        <PhotosPanel
          spreadId={spreadId}
          onAnyDropdownOpenChange={onPhotosDropdownOpenChange}
          escapeStateRef={photosEscapeStateRef}
        />
      )
    case RIGHT_PANEL_IDS.clikart:
      return (
        <ClikArtPanel {...clikArtFilters} onAnyDropdownOpenChange={onClikArtDropdownOpenChange} />
      )
    case RIGHT_PANEL_IDS.adorders:
      return <NewsPanel />
    case RIGHT_PANEL_IDS.templates:
      return (
        <TemplatesPanel
          {...templatesFilters}
          onAnyDropdownOpenChange={onTemplatesDropdownOpenChange}
        />
      )
    case RIGHT_PANEL_IDS.backgrounds:
      return (
        <BackgroundsPanel
          onAnyDropdownOpenChange={onBackgroundsDropdownOpenChange}
          settings={backgroundsSettings}
          onSettingsChange={onBackgroundsSettingsChange}
        />
      )
    case RIGHT_PANEL_IDS.portraits:
      return (
        <PortraitsPanel
          {...portraitsFilters}
          onAnyDropdownOpenChange={onPortraitsDropdownOpenChange}
        />
      )
    case RIGHT_PANEL_IDS.spreads:
      return <BooksPanel />
    default:
      return null
  }
}
